import { Form, Formik } from "formik";
import { handleSubmitForm } from "helpers/apiUtils";
import { useQueryParam } from "hooks/useQueryParam";
import { useSmartBack } from "hooks/useSmartBack";
import { Row, Col, Spinner } from "react-bootstrap";
import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getVehicleModelValidationSchema } from "utils/validations";
import CustomField from "components/form/CustomField";
import SingleSelect from "components/common/SingleSelect";
import CustomLabel from "components/form/CustomLabel";
import CustomError from "components/form/CustomError";
import { usePermission } from "hooks/usePermission";
import { getVehicleMakeDropdownlist } from "store/slices/vehicleMakeSlice";
import {
  addVehicleModel,
  getVehicleModelDetails,
  updateVehicleModel,
} from "store/slices/vehicleModelSlice";
import PageHeader from "components/common/PageHeader";

function AddEditVehicleModel() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const id = useQueryParam("id");
  const isEdit = Boolean(id);
  const goBack = useSmartBack();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canAdd, canEdit } = canAction("vehicle");

  const {
    isLoading,
    loading,
    itemDetails: vehicleModelDetails,
  } = useSelector((state) => state.vehicleModel);
  const { itemsList: vehicleMakeDropdownList } = useSelector(
    (state) => state.VehicleMake,
  );

  const [initialValues, setInitialValues] = useState({
    name: {
      en: "",
      ar: "",
    },
    makeId: "",
  });

  useEffect(() => {
    dispatch(getVehicleMakeDropdownlist());
    if (isEdit && id) {
      dispatch(getVehicleModelDetails(id));
    }
  }, [isEdit, id, dispatch]);

  useEffect(() => {
    if (isEdit && vehicleModelDetails) {
      setInitialValues({
        name: {
          en: vehicleModelDetails?.name?.en || "",
          ar: vehicleModelDetails?.name?.ar || "",
        },
        makeId: vehicleModelDetails?.makeId?._id || "",
      });
    }
  }, [isEdit, vehicleModelDetails, vehicleMakeDropdownList]);

  const handleSubmit = (values) => {
    const payload = {
      name: {
        en: values.name.en,
        ar: values.name.ar,
      },
      makeId: values.makeId,
    };

    if (isEdit) {
      const updatedVehicleModelPayload = {
        id,
        ...values,
      };
      handleSubmitForm({
        dispatch,
        action: updateVehicleModel,
        payload: updatedVehicleModelPayload,
        onSuccess: () => navigate(-1),
      });
    } else {
      handleSubmitForm({
        dispatch,
        action: addVehicleModel,
        payload,
        onSuccess: () => navigate(-1),
      });
    }
  };
  if (!canEdit && isEdit) return null;
  if (!canAdd && !isEdit) return null;

  if (isEdit && loading)
    return (
      <div className="g_center my-5">
        <Spinner />;
      </div>
    );

  return (
    <div className="page-content">
      <PageHeader
        isBackBtn={true}
        title={isEdit ? t("Edit Vehicle Model") : t("Add Vehicle Model")}
      />
      <h3 className="page_sub_title">
        {/* {t("Manage vehicle model configurations")} */}
      </h3>

      <div className="common_card">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={getVehicleModelValidationSchema(t, isEdit)}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Row>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Name EN")}
                      name="name.en"
                      placeholder={t("Enter title")}
                      enableTranslate
                      translateTo="ar"
                      targetField="name.ar"
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Name AR")}
                      name="name.ar"
                      placeholder={t("Enter Arabic title")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="name.en"
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomLabel htmlFor="make" label={t("Make")} isRequired />{" "}
                    <SingleSelect
                      value={values.makeId}
                      name="makeId"
                      options={vehicleMakeDropdownList?.map((option) => ({
                        name:
                          language === "ar"
                            ? option?.name?.ar
                            : option?.name?.en,
                        value: option?._id,
                      }))}
                      handleSingleSelectChange={(e) => {
                        setFieldValue("makeId", e.value);
                      }}
                      placeholder={t("Make")}
                      className="custom_dropdown"
                    />
                    <CustomError name="makeId" />
                  </div>
                </Col>

                <Col lg={12} className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>

                  <button
                    type="submit"
                    className="main_btn"
                    disabled={isLoading}
                  >
                    {isEdit ? t("Save Changes") : t("Create Vehicle Model")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default AddEditVehicleModel;
